import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Button, Dropdown, Spin, theme, Typography } from 'antd';
import { BellOutlined, CloseOutlined } from '@ant-design/icons';
import { IconBellOff, IconTrash } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contextProviders/AuthProvider';
import { message } from '../services/toastMessage';
import ConfirmBottomSheet from './ConfirmBottomSheet';
import EmptyState from './EmptyState';
import BookingReferenceTag from './BookingReferenceTag';
import SwipeToDeleteRow from './SwipeToDeleteRow';
import {
  deleteAllNotifications,
  deleteNotification,
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../services/NotificationService';
import { onReceiveNotification } from '../services/RealtimeService';
import { playNotificationSound } from '../services/NotificationSoundService';
import { IHeavyDutyMachineryNotification } from '../interfaces/heavyDutyMachinery/INotification';
import { TRANSLATIONS } from '../constants/translations';
import { NOTIF_TITLE_KEYS } from '../constants/notificationTitles';
import { translate } from '../helpers/translationHelper';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';
import { TEAL, TEAL_SOFT_BG } from '../theme/creamyTokens';

const { Text } = Typography;

const POLL_INTERVAL_MS = 60 * 1000;
// Same idea as Messages.tsx's own LOAD_MORE_THRESHOLD_PX -- how close to the
// bottom of the mobile overlay's own scroll (px) triggers the next page.
// Desktop's dropdown stays on just its first page for now (see refresh/
// loadMore below), so this only ever matters there.
const LOAD_MORE_THRESHOLD_PX = 200;

// t() is called directly here (not through the translate() helper) since
// these need i18next's {{count}}/{{title}} interpolation, which translate()
// doesn't expose.
type TFunc = (key: string, opts?: Record<string, unknown>) => string;

const formatNotifTime = (createdAt: string, t: TFunc): string => {
  const diffMs = Date.now() - new Date(createdAt).getTime();
  const minutes = Math.floor(diffMs / (60 * 1000));
  const hours = Math.floor(diffMs / (60 * 60 * 1000));
  const days = Math.floor(diffMs / (24 * 60 * 60 * 1000));

  if (minutes < 1) return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TIME_JUST_NOW);
  if (hours < 1) return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TIME_MINUTES, { count: minutes });
  if (days < 1) return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TIME_HOURS, { count: hours });
  return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TIME_DAYS, { count: days });
};


// actorName/contentTitle now carry real per-event data for most types (see
// NotificationHelper's call sites in ConversationsController,
// BookingConfirmationHelper, BookingCancellationHelper and
// AdminQualityAuditsController) instead of every notification saying the
// same fixed sentence regardless of who/what triggered it. Each case below
// prefers the specific phrasing when that data is present, falling back to
// the old generic one when it isn't (e.g. an admin's forced cancel has no
// single "actor" worth naming to the host).
const notifBody = (n: IHeavyDutyMachineryNotification, t: TFunc): string => {
  switch (n.type) {
    case 'NewBooking':
      return n.actorName
        ? t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_NEW_BOOKING_BY, { actor: n.actorName, title: n.contentTitle })
        : t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_NEW_BOOKING, { title: n.contentTitle });
    case 'NewMessage':
      // contentTitle IS a preview of the message itself here -- the
      // sender's own free text, shown as-is rather than interpolated into
      // a template sentence (same idea as AdminAnnouncement's body below).
      return n.contentTitle || t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_NEW_MESSAGE);
    case 'BookingStatusChanged':
      return n.actorName
        ? t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_BOOKING_CANCELLED_BY, { actor: n.actorName, title: n.contentTitle })
        : t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_BOOKING_STATUS_CHANGED, { title: n.contentTitle });
    case 'AuditPassed':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_AUDIT_PASSED, { title: n.contentTitle });
    case 'AuditFailed':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_AUDIT_FAILED, { title: n.contentTitle });
    case 'AdminAnnouncement':
      return n.body ?? '';
    case 'PayoutsDisabled':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_PAYOUTS_DISABLED);
    case 'PayoutsRestored':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_PAYOUTS_RESTORED);
    case 'BookingConfirmed':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_BOOKING_CONFIRMED, { title: n.contentTitle });
    case 'BookingPaymentFailed':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_BOOKING_PAYMENT_FAILED, { title: n.contentTitle });
    case 'SpaceApproved':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SPACE_APPROVED, { title: n.contentTitle });
    case 'SpaceRejected':
      // n.body carries the admin's own reason (AdminSpacesController.RejectSpace) --
      // appended after the templated sentence, same idea as AuditFailed's notes.
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SPACE_REJECTED, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'AdminMessage':
      // contentTitle IS a preview of the admin's own message text, same
      // convention as NewMessage above.
      return n.contentTitle || t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_ADMIN_MESSAGE);
    case 'GeofenceAlert':
      // n.body carries how far outside it was -- the difference between a
      // lorry two streets away and a machine in another province, which is
      // the only thing the host needs before deciding whether to panic.
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_GEOFENCE_ALERT, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'GeofenceResolved':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_GEOFENCE_RESOLVED, { title: n.contentTitle });
    case 'GeofenceZoneSet':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_GEOFENCE_ZONE_SET, { title: n.contentTitle });
    case 'GeofenceMoveRequested':
      // n.body is the guest's own reason, which is the whole substance of it.
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_GEOFENCE_MOVE_REQUESTED, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'GeofenceMoveApproved':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_GEOFENCE_MOVE_APPROVED, { title: n.contentTitle });
    case 'GeofenceMoveRejected':
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_GEOFENCE_MOVE_REJECTED, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'OvertimeChargeFailed':
      // n.body carries the formatted overtime amount (OvertimeChargeHelper.
      // MarkFailedAsync), n.contentTitle the machine's title -- both
      // interpolated into one sentence rather than shown as two separate lines.
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_OVERTIME_CHARGE_FAILED, { title: n.contentTitle, amount: n.body });
    case 'NewQuoteRequest':
      // actorName is the guest who asked; contentTitle the machine. Named
      // when we have it, same convention as NewBooking above.
      return n.actorName
        ? t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_NEW_QUOTE_REQUEST_BY, { actor: n.actorName, title: n.contentTitle })
        : t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_NEW_QUOTE_REQUEST, { title: n.contentTitle });
    case 'QuoteAccepted':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_QUOTE_ACCEPTED, { title: n.contentTitle });
    case 'QuoteRevised':
      // n.body carries the host's own required reason for the new amount
      // (BookingController.ProposeQuoteAmount) -- that reason IS the point
      // of the notification, so it's appended rather than dropped.
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_QUOTE_REVISED, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'TransportStarted':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_TRANSPORT_STARTED, { title: n.contentTitle });
    case 'OffRentDeclared':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_OFF_RENT_DECLARED, { title: n.contentTitle });
    case 'CollectionStarted':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_COLLECTION_STARTED, { title: n.contentTitle });
    case 'FundsReleased':
      // n.body is the already-formatted payout amount + currency
      // (BookingFundsReleaseService); absent when the booking has no
      // HostPayoutAmount, hence the amount-less fallback sentence.
      return n.body
        ? t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_FUNDS_RELEASED_AMOUNT, { title: n.contentTitle, amount: n.body })
        : t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_FUNDS_RELEASED, { title: n.contentTitle });
    case 'TransferPendingResolution':
      // n.body is "amount currency -- reason" (BookingLineTransferService).
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_TRANSFER_PENDING, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'BookingCharged':
      // n.body es el importe ya formateado (BookingCaptureHelper).
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_BOOKING_CHARGED, { title: n.contentTitle, amount: n.body });
    case 'BookingFundsHeld':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_FUNDS_HELD, { title: n.contentTitle });
    case 'BookingAuthorizationFailed':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_AUTH_FAILED, { title: n.contentTitle });
    case 'HostDepositFunded':
      // n.body es el importe ya formateado (HandleHostDepositSucceeded).
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_HOST_DEPOSIT_FUNDED, { amount: n.body });
    case 'HostDepositCharged':
      // Lo que se le ha descontado y por qué máquina. Un saldo que baja sin
      // avisar es como un host se entera de esto semanas después, al no poder
      // aceptar un trabajo.
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_HOST_DEPOSIT_CHARGED, { amount: n.body, title: n.contentTitle });
    case 'BookingRefunded':
      // n.body is "amount currency — reason" (BookingRefundHelper). The
      // reason is the whole substance of this one -- a number on its own
      // tells neither party which of the things that could have gone wrong
      // did -- so it is appended rather than dropped.
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_BOOKING_REFUNDED, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'DisputeClosedByDeadline':
      // n.body is the claimed amount + currency (DisputeDeadlineService).
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_DISPUTE_CLOSED_BY_DEADLINE, { title: n.contentTitle, amount: n.body });
    case 'OperatorReinstated':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_OPERATOR_REINSTATED, { title: n.contentTitle });
    case 'ServiceProviderReinstated':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_PROVIDER_REINSTATED);
    case 'ServiceProviderSuspended':
      // n.body carries the admin's own reason -- appended, because a provider
      // whose whole catalogue just vanished has to be told why.
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_PROVIDER_SUSPENDED)} ${n.body ?? ''}`.trim();
    case 'OperatorSuspended':
      // n.body carries the admin's own reason, same as SpaceRejected above.
      // Appending it rather than dropping it is the point: a host whose
      // operator stops being offered has to be told why, not just that.
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_OPERATOR_SUSPENDED, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'HandoverAcknowledged':
    case 'HandoverRejected':
      // n.body carries the counterparty's own remarks, which is the whole
      // point when they accepted with reservations or rejected outright --
      // absent on a clean acceptance, hence the append rather than a slot.
      return `${t(
        n.type === 'HandoverRejected'
          ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_HANDOVER_REJECTED
          : TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_HANDOVER_ACKNOWLEDGED,
        { title: n.contentTitle }
      )} ${n.body ?? ''}`.trim();
    case 'DepositAuthorizationFailed':
      // Names the machine and says what to do about it: the guest is the only
      // one who can put a working card on file, and this arrives two days out
      // precisely so there is still time to.
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_DEPOSIT_AUTH_FAILED, { title: n.contentTitle });
    case 'NewServiceRequest':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_NEW_SERVICE_REQUEST, {
        actor: n.actorName,
        title: n.contentTitle,
      });
    case 'ServiceCompleted':
      // Names the job and says what the customer's next 24 hours are for. They
      // are the only one who can say something is wrong, and this is the window
      // in which saying it still stops the money (BookingFundsReleaseService).
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SERVICE_COMPLETED, {
        actor: n.actorName,
        title: n.contentTitle,
      });
    case 'ServiceRequestQuoted':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SERVICE_REQUEST_QUOTED, {
        actor: n.actorName,
        amount: n.contentTitle,
      });
    case 'ServiceRequestAccepted':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SERVICE_REQUEST_ACCEPTED, {
        actor: n.actorName,
        amount: n.contentTitle,
      });
    case 'ServiceRequestDeclined':
    case 'ServiceRequestCancelled':
      // n.body is the other side's own written reason, and it is the half that
      // matters -- "no aceptó" tells a professional nothing they can act on.
      return `${t(
        n.type === 'ServiceRequestDeclined'
          ? TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SERVICE_REQUEST_DECLINED
          : TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SERVICE_REQUEST_CANCELLED,
        { actor: n.actorName }
      )} ${n.body ?? ''}`.trim();
    case 'ServiceRequestExpired':
      // No interpolation: a request can be sent to a professional rather than
      // to one of their listings, in which case there is no title to name.
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SERVICE_REQUEST_EXPIRED);
    case 'ServiceVisitScheduled':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SERVICE_VISIT_SCHEDULED, {
        actor: n.actorName,
        amount: n.contentTitle,
      });
    case 'ServiceVisitNotConfirmed':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SERVICE_VISIT_NOT_CONFIRMED);
    case 'ServiceVisitConfirmationMissed':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_SERVICE_VISIT_MISSED);
    case 'DisputeOpened':
      // n.body is the claimant's own reason (BookingDisputeController).
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_DISPUTE_OPENED, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'DisputeAnswered':
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_DISPUTE_ANSWERED, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    case 'DisputeOffer':
      // From here down n.body is a preformatted "amount currency".
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_DISPUTE_OFFER, { title: n.contentTitle, amount: n.body });
    case 'DisputeSettled':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_DISPUTE_SETTLED, { title: n.contentTitle, amount: n.body });
    case 'DisputeSplit':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_DISPUTE_SPLIT, { title: n.contentTitle, amount: n.body });
    case 'DisputeWithdrawn':
      return t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_DISPUTE_WITHDRAWN, { title: n.contentTitle });
    case 'DisputeResolved':
      // n.body is the reviewer's own written reason.
      return `${t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_BODY_DISPUTE_RESOLVED, { title: n.contentTitle })} ${n.body ?? ''}`.trim();
    default:
      // Never an empty row. A type this build doesn't know about (a backend
      // deployed ahead of this bundle, or one simply forgotten here) still
      // shows whatever real text came with it instead of rendering blank,
      // which is indistinguishable from a broken notification.
      return n.body ?? n.contentTitle ?? '';
  }
};

const notifTitle = (n: IHeavyDutyMachineryNotification, t: TFunc): string => {
  if (n.type === 'AdminAnnouncement' && n.contentTitle) return n.contentTitle;
  // Chat-app convention (WhatsApp/Telegram/iOS): the sender's own name
  // headlines a new message instead of a generic "New message" category
  // label -- falls back to that generic title on the rare notification
  // whose sender has no display name set.
  if (n.type === 'NewMessage' && n.actorName) return n.actorName;
  // Same "never blank" guarantee as notifBody's own default: an unmapped
  // type would otherwise reach t(undefined) and render an empty headline.
  const key = NOTIF_TITLE_KEYS[n.type];
  return key ? t(key) : t(TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIF_TITLE_GENERIC);
};

/**
 * Bell icon shown in AppLayout's header, next to the language switcher --
 * ported from Authentication76's Marketplace NotificationBell.
 *
 * Both sizes now use the same BellOutlined, just at different sizes. The
 * phone used to carry SpaBubbleIcon instead -- two overlapping outlined
 * circles, a leftover of the spa app this was built out of -- which meant
 * the same control was a bell on one screen size and something
 * unrecognisable on the other. On isMobile it still opens, instead of a
 * dropdown panel, the exact same full-screen overlay mechanism as
 * HomeSearchBar's own mobile control: a plain fade, an "x" top-right,
 * nothing visible behind it. Desktop/tablet keep the Dropdown panel.
 *
 * The unread badge is teal on both, not antd's default red: red is for
 * negative alerts elsewhere in the app and these are only informational.
 * It used to be teal on the phone and red on the desktop, which made the
 * same count look like a warning on one screen size and a tally on the
 * other.
 */
const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { userId } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useAppBreakpoint();

  const [notifications, setNotifications] = useState<IHeavyDutyMachineryNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  // Mobile swipe-to-delete: at most one row's delete action revealed at a
  // time (opening another row's closes whichever was open) -- same pattern
  // as Messages.tsx's conversation list.
  const [openSwipeId, setOpenSwipeId] = useState<string | null>(null);
  // Mobile-only "delete all" confirm (see the mobile-gated button in
  // renderNotificationList below) -- unlike a single swipe-to-delete, this
  // is a bulk irreversible action, so it gets the same ConfirmBottomSheet
  // gate as cancelling a booking or hiding a conversation instead of firing
  // straight off a tap.
  const [deleteAllSheetOpen, setDeleteAllSheetOpen] = useState(false);

  const refresh = useCallback(async () => {
    if (!userId) return;
    const [page, count] = await Promise.all([getNotifications(), getUnreadNotificationCount()]);
    setNotifications(page.items);
    setHasMore(page.hasMore);
    setUnreadCount(count);
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [userId, refresh]);

  // Live push on top of the polling above: a new notification appears
  // instantly instead of waiting up to POLL_INTERVAL_MS.
  useEffect(() => {
    if (!userId) return undefined;
    return onReceiveNotification((notification) => {
      setNotifications((prev) => [notification, ...prev]);
      setUnreadCount((prev) => prev + 1);
      playNotificationSound();
    });
  }, [userId]);

  // Infinite scroll's own next-page fetch (see handleListScroll below) --
  // guarded on loadingMore so a fast scroll firing several times while a
  // page is already in flight doesn't queue up duplicate requests. Reached
  // from the mobile overlay only for now -- the desktop dropdown is a small
  // fixed-height panel where paging further back is far less likely to
  // matter, so it stays on just its first page.
  const loadMore = async () => {
    if (loadingMore || !hasMore || notifications.length === 0) return;
    setLoadingMore(true);
    try {
      const cursor = notifications[notifications.length - 1].id;
      const page = await getNotifications(cursor);
      setNotifications((prev) => [...prev, ...page.items]);
      setHasMore(page.hasMore);
    } catch {
      // Silent, same as Messages.tsx's own loadMore -- a failed "load more"
      // isn't worth a toast, the user can just keep scrolling to retry.
    } finally {
      setLoadingMore(false);
    }
  };

  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < LOAD_MORE_THRESHOLD_PX) loadMore();
  };

  if (!userId) return null;

  const handleNotificationClick = async (n: IHeavyDutyMachineryNotification) => {
    if (!n.read) {
      await markNotificationRead(n.id);
      await refresh();
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  // Optimistic, same rollback-on-failure shape as handleDelete below -- every
  // badge/dot tied to unreadCount or a row's read state has to flip the
  // instant this is tapped, not after a round trip, or it reads as
  // unresponsive on a slow connection.
  const handleMarkAllRead = async () => {
    const previousNotifications = notifications;
    const previousUnreadCount = unreadCount;
    setNotifications((prev) => prev.map((n) => (n.read ? n : { ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch {
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_MARK_ALL_READ_ERROR));
    }
  };

  // Also optimistic (list empties the instant the sheet is confirmed), but
  // unlike handleMarkAllRead this is destructive and irreversible, so it's
  // gated behind deleteAllSheetOpen instead of firing straight off a tap.
  const handleDeleteAll = async () => {
    const previousNotifications = notifications;
    const previousUnreadCount = unreadCount;
    const previousHasMore = hasMore;
    setDeleteAllSheetOpen(false);
    setNotifications([]);
    setUnreadCount(0);
    setHasMore(false);
    try {
      await deleteAllNotifications();
    } catch {
      setNotifications(previousNotifications);
      setUnreadCount(previousUnreadCount);
      setHasMore(previousHasMore);
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ALL_ERROR));
    }
  };

  // Optimistic, same pattern as Messages.tsx's handleConfirmHide -- the row
  // disappears the instant the swipe action is tapped, before the backend
  // call resolves; on failure it's spliced back into its original position
  // and a toast explains why. Unlike hiding a conversation this is a real
  // delete (see deleteNotification's own doc comment), so there's no
  // confirmation sheet first -- the swipe-then-tap gesture itself is enough
  // confirmation for a single item, same as Gmail/Twitter's own
  // notification lists.
  const handleDelete = async (n: IHeavyDutyMachineryNotification) => {
    const originalIndex = notifications.findIndex((x) => x.id === n.id);
    setOpenSwipeId(null);
    setNotifications((prev) => prev.filter((x) => x.id !== n.id));
    if (!n.read) setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await deleteNotification(n.id);
    } catch {
      setNotifications((prev) => {
        const next = [...prev];
        next.splice(Math.min(originalIndex, next.length), 0, n);
        return next;
      });
      if (!n.read) setUnreadCount((prev) => prev + 1);
      message.error(translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ERROR));
    }
  };

  const renderRow = (n: IHeavyDutyMachineryNotification, mobile: boolean) => {
    const content = (
      <div
        onClick={() => handleNotificationClick(n)}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          padding: mobile ? '10px 16px' : '10px 8px 10px 16px',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <Text strong style={{ fontSize: 13 }}>
            {notifTitle(n, t)}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
            {formatNotifTime(n.createdAt, t)}
          </Text>
        </div>
        <Text style={{ fontSize: 13, display: 'block' }}>{notifBody(n, t)}</Text>
        {/* Which rental this is about. Null on anything that isn't about a
            booking, and on every notification raised before the backend
            started recording it -- those rows simply look as they always
            did. A host with three of the same excavator out at once is
            exactly who could not tell these apart. */}
        <BookingReferenceTag reference={n.bookingReference} style={{ display: 'block', marginTop: 2 }} />
      </div>
    );

    const row = (
      // Same TEAL this component already uses for the mobile unread badge
      // dot -- AntD's default blue highlight here had nothing to do with the
      // app's coral/teal theme.
      <div
        className={mobile ? undefined : 'heavyDutyMachinery-hover-actions-row'}
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          background: n.read ? undefined : TEAL_SOFT_BG,
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        {content}
        {/* Desktop's answer to the phone's swipe-to-delete: the same single
            delete, revealed by the pointer that only exists here. Hidden
            until hover (or keyboard focus) so a list of notifications does
            not read as a list of delete buttons. */}
        {!mobile && (
          <button
            type="button"
            className="heavyDutyMachinery-hover-actions-row__action"
            aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ACTION)}
            title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ACTION)}
            onClick={(event) => {
              event.stopPropagation();
              handleDelete(n);
            }}
            style={{
              flexShrink: 0,
              width: 30,
              height: 30,
              margin: '8px 10px 0 0',
              borderRadius: '50%',
              border: 'none',
              background: 'transparent',
              color: token.colorTextTertiary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <IconTrash size={15} />
          </button>
        )}
      </div>
    );

    if (!mobile) return row;
    return (
      <SwipeToDeleteRow
        key={n.id}
        isOpen={openSwipeId === n.id}
        onOpenChange={(isOpen) => setOpenSwipeId(isOpen ? n.id : null)}
        onDeleteClick={() => handleDelete(n)}
        deleteLabel={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ACTION)}
      >
        {row}
      </SwipeToDeleteRow>
    );
  };

  // Shared between the desktop dropdown panel and the mobile full-screen
  // overlay -- title/mark-all-read/delete-all row, then either the empty
  // state or the list itself. Both sizes get every action; what differs is
  // only how a single row is deleted (swipe on a phone, hover on a mouse,
  // see renderRow) and the "load more" spinner (mobile, see handleListScroll
  // above).
  const renderNotificationList = (mobile: boolean) => (
    <>
      {/* flexWrap plus a title that refuses to shrink: with both actions
          showing, the desktop panel is 340px of which the two links want most,
          and a plain space-between row squeezed the title down to almost
          nothing -- which does not truncate, it wraps, one character per line.
          The actions drop to their own line instead. */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          rowGap: 4,
          padding: '12px 16px',
          borderBottom: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Text strong style={{ flexShrink: 0 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_TITLE)}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginLeft: 'auto' }}>
          {unreadCount > 0 && (
            <Button type="link" size="small" style={{ padding: 0 }} onClick={handleMarkAllRead}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_MARK_ALL_READ)}
            </Button>
          )}
          {notifications.length > 0 && (
            <Button type="link" danger size="small" style={{ padding: 0 }} onClick={() => setDeleteAllSheetOpen(true)}>
              {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ALL_ACTION)}
            </Button>
          )}
        </div>
      </div>
      {notifications.length === 0 ? (
        <EmptyState
          icon={<IconBellOff size={24} />}
          title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_EMPTY)}
          description={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_EMPTY_DESCRIPTION)}
        />
      ) : (
        notifications.map((n) => <React.Fragment key={n.id}>{renderRow(n, mobile)}</React.Fragment>)
      )}
      {mobile && loadingMore && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0' }}>
          <Spin size="small" />
        </div>
      )}
    </>
  );

  const dropdownRender = () => (
    <div
      style={{
        width: 380,
        maxHeight: 440,
        overflowY: 'auto',
        background: token.colorBgElevated,
        borderRadius: 16,
        boxShadow: token.boxShadowSecondary,
        overflowX: 'hidden',
      }}
    >
      {renderNotificationList(false)}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_TITLE)}
          onClick={() => setOpen(true)}
          style={{ border: 'none', background: 'none', padding: 6, display: 'flex', cursor: 'pointer' }}
        >
          <Badge count={unreadCount} size="small" offset={[-2, 2]} color={TEAL}>
            <BellOutlined style={{ fontSize: 22, color: token.colorText }} />
          </Badge>
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              key="mobile-notifications-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: 'easeOut' }}
              style={{
                position: 'fixed',
                inset: 0,
                zIndex: 2000,
                background: token.colorBgContainer,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '16px 16px 0', flexShrink: 0 }}>
                <button
                  type="button"
                  aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_OVERLAY_CLOSE)}
                  onClick={() => setOpen(false)}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: 'none',
                    background: token.colorFillTertiary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <CloseOutlined style={{ fontSize: 16 }} />
                </button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto' }} onScroll={handleListScroll}>
                {renderNotificationList(true)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <ConfirmBottomSheet
          open={deleteAllSheetOpen}
          title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ALL_SHEET_TITLE)}
          cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ALL_SHEET_KEEP)}
          confirmText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ALL_SHEET_CONFIRM)}
          onConfirm={handleDeleteAll}
          onClose={() => setDeleteAllSheetOpen(false)}
        />
      </>
    );
  }

  return (
    <>
      <Dropdown open={open} onOpenChange={setOpen} trigger={['click']} dropdownRender={dropdownRender}>
        <Badge count={unreadCount} size="small" offset={[-2, 2]} color={TEAL}>
          <Button type="text" icon={<BellOutlined style={{ fontSize: 18 }} />} />
        </Badge>
      </Dropdown>

      {/* Same confirmation as the phone's: this sheet portals onto <body>, so
          it is not clipped by the dropdown panel it is opened from. */}
      <ConfirmBottomSheet
        open={deleteAllSheetOpen}
        title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ALL_SHEET_TITLE)}
        cancelText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ALL_SHEET_KEEP)}
        confirmText={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_NOTIFICATIONS_DELETE_ALL_SHEET_CONFIRM)}
        onConfirm={handleDeleteAll}
        onClose={() => setDeleteAllSheetOpen(false)}
      />
    </>
  );
};

export default NotificationBell;
